import React, { useState, useEffect } from 'react';
import { getData } from '../../../../services/apiService';
import useFileDownload from '../../../../commonFn/useFileDownload';
import { AlertCircle, CheckCircle } from 'lucide-react';

// Import all components
import Header from './Header';
import SearchBar from './SearchBar';
import EventTable from './EventTable';
import Pagination from './Pagination';
import EventDetailsModal from './EventDetailsModal';
import SettlementModal from './SettlementModal';

const EventReports = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showEventDetailsModal, setShowEventDetailsModal] = useState(false);
  const [showSettlementModal, setShowSettlementModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  // State for API data
  const [events, setEvents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Settlement modal state
  const [settlementFromDate, setSettlementFromDate] = useState('');
  const [settlementToDate, setSettlementToDate] = useState('');
  const [selectedFormat, setSelectedFormat] = useState('pdf');

  // Custom hook for downloading reports
  const { downloadFile: downloadEventPayReport, loading: eventPayLoading } = useFileDownload({
    url: `Report/GetEvePayDetails?format=`,
    defaultFilename: 'event_payments_report'
  });

  const { downloadFile: downloadEventSpecificReport, loading: eventSpecificLoading } = useFileDownload({
    url: `Report/GetEvePayDetails?EventId=${selectedEvent?.Event_Id}&format=`,
    defaultFilename: 'event_payments'
  });

  // Fetch events on component mount
  useEffect(() => {
    fetchEvents();
  }, []);

  // Fetch events from API
  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getData('Event/GetEventRevenueDetails');

      if (response && !response.isError && response.data) {
        setEvents(response.data);
        setCurrentPage(1);
      } else {
        setError('No event data available');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching events:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle download report for all events
  const handleDownloadReport = (format) => {
    downloadEventPayReport(format);
  };

  // Handle download for specific event
  const handleDownloadEventReport = (eventId, format) => {
    downloadEventSpecificReport(format);
  };

  // Fetch transactions for a specific event
  const fetchTransactions = async (eventId) => {
    try {
      setLoadingTransactions(true);
      const response = await getData(`Event/GetEventPayment?eventId=${eventId}`);

      if (response && !response.isError && response.data) {
        const { Summary, Payments } = response.data;

        // Transform the payment data
        const transformedPayments = Payments.map(payment => ({
          TransactionId: payment.Txn_Id,
          ReferenceNumber: `REG-${payment.Reg_Id}`,
          TransactionDate: payment.Txn_Date,
          TransactionTime: new Date(payment.Txn_Date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
          }),
          CustomerName: payment.Name,
          ParticipantType: payment.ParticipantType,
          Amount: payment.Txn_Amount,
          SettledAmount: payment.Settled_Amount,
          Status: payment.Status === 1 ? 'successful' : payment.Status === 2 ? 'failed' : payment.Status === 3 && 'pending',
          GstAmount: payment.Gst_Amt,
          GstPercentage: payment.Gst_Perc,
          RegistrationId: payment.Reg_Id,
          EventId: payment.Event_Id,
          Settled_Date: payment.Settled_Date
        }));

        setTransactions(transformedPayments);
        return {
          summary: Summary,
          payments: transformedPayments
        };
      } else {
        return { summary: null, payments: [] };
      }
    } catch (err) {
      console.error('Error fetching transactions:', err);
      return { summary: null, payments: [] };
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Handle view details click
  const handleViewDetails = async (event) => {
    setSelectedEvent(event);
    setShowEventDetailsModal(true);

    // Fetch transactions for this event
    const { summary, payments } = await fetchTransactions(event.Event_Id);

    // Update the selected event with transactions and summary
    setSelectedEvent(prev => ({
      ...prev,
      summary: summary,
      transactions: payments
    }));
  };

  // Handle settlement details click
  const handleSettlementDetails = (event = null) => {
    setSelectedEvent(event);
    setSettlementFromDate('');
    setSettlementToDate('');
    setSelectedFormat('pdf');
    setShowSettlementModal(true);
  };

  // Filter events for reports
  const filteredEvents = events.filter(event => {
    const matchesSearch = searchTerm === '' ||
      event.Event_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.Event_Sname?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
  const indexOfLastEvent = currentPage * itemsPerPage;
  const indexOfFirstEvent = indexOfLastEvent - itemsPerPage;
  const currentEvents = filteredEvents.slice(indexOfFirstEvent, indexOfLastEvent);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const PaymentStatusBadge = ({ status }) => {
    const config = {
      1: { color: 'bg-green-100 text-green-800', label: 'Successful', icon: CheckCircle },
      2: { color: 'bg-yellow-100 text-yellow-800', label: 'Pending', icon: AlertCircle },
      0: { color: 'bg-red-100 text-red-800', label: 'Canceled', icon: AlertCircle }
    };

    const { color, label, icon: Icon } = config[status] || config[2];

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${color} flex items-center gap-1`}>
        <Icon size={12} />
        {label}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <Header
          onSettlementClick={handleSettlementDetails}
          onDownloadReport={handleDownloadReport}
          eventPayLoading={eventPayLoading}
        />

        <SearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
        />

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="text-center">
              <div className="relative mx-auto w-16 h-16">
                <div className="absolute inset-0 border-4 border-blue-100 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
              </div>
              <p className="mt-4 text-gray-600 font-medium">Loading events...</p>
            </div>
          </div>
        ) : error ? (
          <div className="bg-gradient-to-br from-red-50 to-white rounded-xl p-6 text-center border border-red-100 shadow-sm">
            <div className="inline-flex p-3 bg-red-100 rounded-full mb-3">
              <AlertCircle className="text-red-600" size={32} />
            </div>
            <h3 className="text-lg font-semibold text-red-800 mb-2">Error Loading Events</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchEvents}
              className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:shadow-md transition-shadow"
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            <EventTable
              events={currentEvents}
              onViewDetails={handleViewDetails}
              formatDate={formatDate}
              formatCurrency={formatCurrency}
              totalEvents={filteredEvents.length}
              currentPage={currentPage}
              totalPages={totalPages}
            />

            {filteredEvents.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                indexOfFirstEvent={indexOfFirstEvent}
                indexOfLastEvent={indexOfLastEvent}
                totalItems={filteredEvents.length}
                itemsPerPage={itemsPerPage}
              />
            )}
          </>
        )}
      </div>

      {/* Modals */}
      {showEventDetailsModal && selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          onClose={() => {
            setShowEventDetailsModal(false);
            setSelectedEvent(null);
            setTransactions([]);
          }}
          loadingTransactions={loadingTransactions}
          formatDate={formatDate}
          formatTime={formatTime}
          formatCurrency={formatCurrency}
          PaymentStatusBadge={PaymentStatusBadge}
          onDownloadEventReport={handleDownloadEventReport}
          eventSpecificLoading={eventSpecificLoading}
        />
      )}

      {showSettlementModal && (
        <SettlementModal
          event={selectedEvent}
          onClose={() => {
            setShowSettlementModal(false);
            setSelectedEvent(null);
          }}
          fromDate={settlementFromDate}
          toDate={settlementToDate}
          onFromDateChange={setSettlementFromDate}
          onToDateChange={setSettlementToDate}
          selectedFormat={selectedFormat}
          onFormatChange={setSelectedFormat}
          formatDate={formatDate}
        />
      )}
    </div>
  );
};

export default EventReports;